import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import {
  AppBar,
  Dialog,
  DialogContent,
  DialogProps,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TranslateIcon from "@mui/icons-material/Translate";

import { DialogTransition } from "./primitives/DialogTransition";
import { useAppContext } from "../context/AppContext";
import ChangePassword from "./ChangePassword";
import { useLoginContext } from "../context/LoginContext";

export const SettingsDialog: React.FC<Omit<DialogProps, "open">> = ({
  ...dialogProps
}) => {
  const { t, i18n } = useTranslation();

  const { toggleSettingsDialog, settingsDialog } = useAppContext();
  const { isLoggedIn } = useLoginContext();

  const onClose = useCallback(() => {
    toggleSettingsDialog();
  }, [toggleSettingsDialog]);

  return (
    <Dialog
      TransitionComponent={DialogTransition}
      onClose={onClose}
      open={settingsDialog}
      {...dialogProps}
    >
      <AppBar elevation={0} sx={{ position: "relative" }}>
        <Toolbar>
          <Typography sx={{ mr: 2, flex: 1 }} variant="h6" component="div">
            {t("settings.title")}
          </Typography>
          <Tooltip title={t("common.close")}>
            <IconButton
              aria-label={`${t("common.close")}`}
              color="inherit"
              edge="end"
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <Typography color="text.secondary" gutterBottom>
          {t("settings.text")}
        </Typography>
        <List
          sx={{
            "& li": {
              mb: 1,
            },
          }}
        >
          <ListItem disableGutters>
            <ListItemIcon>
              <TranslateIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t("settings.language-label")} />
            <ListItemSecondaryAction>
              <TextField
                onChange={(e) => i18n.changeLanguage(e.target.value)}
                select
                value={i18n.resolvedLanguage}
              >
                <MenuItem value="de">{t("settings.languages.de")}</MenuItem>
                <MenuItem value="en">{t("settings.languages.en")}</MenuItem>
                <MenuItem value="fr">{t("settings.languages.fr")}</MenuItem>
                <MenuItem value="it">{t("settings.languages.it")}</MenuItem>
                <MenuItem value="ua">{t("settings.languages.ua")}</MenuItem>
                <MenuItem value="nl">{t("settings.languages.nl")}</MenuItem>
              </TextField>
            </ListItemSecondaryAction>
          </ListItem>
          {isLoggedIn && <ChangePassword />}
        </List>
      </DialogContent>
    </Dialog>
  );
};
